import { isEmpty } from 'radash';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Combobox } from '@/components/core/Combobox';
import { useFloatFormatter } from '@/hooks/useFloatFormatter';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useOptions } from '../hooks/useOptions';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useStableComboboxKey } from '../hooks/useStableComboboxKey';
import { useValidationsStore } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent, Validation } from '../types';
import { normalizePhysicalComboboxValue } from './normalizePhysicalComboboxValue';

interface PhysicalComponentProps extends EditorComponent {
    // Physical specific props go here
    className?: string;
}

interface OptionType {
    label?: string;
    value: string;
}

const PhysicalComponent = ({
    attribute,
    direction = 'horizontal',
    hidden = false,
    hideLabel = false,
    fieldPath = attribute.name,
    gridMode = false,
    isKeyAttribute = false,
    readonly = false,
    beforeAttributeControl,
    handleUrlChange,
    className
}: PhysicalComponentProps) => {

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = !isEmpty(validationErrors);
    const hasWarnings = !isEmpty(validationWarnings);

    const placeholder = usePlaceholder(attribute, readonly);
    const { formatFloat } = useFloatFormatter();

    const comboboxRef = useFocusOnAttribute<HTMLDivElement>(attribute);

    const { control, watch } = useFormContext();
    const selectedValue = watch(fieldPath);

    const { comboboxKey, markUserChange } = useStableComboboxKey(fieldPath, selectedValue);

    const { loading: loadingOptions, options } = useOptions({ attribute, item: attribute.item });
    const [typedValue, setTypedValue] = useState('');

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    useEffect(() => {
        if (!selectedValue || selectedValue === '') {
            setTypedValue('');
        }
    }, [selectedValue, fieldPath]);

    if (hidden) {
        return null;
    }

    const splitToValueAndUnit = (valueString: string) => {
        const firstChar = valueString.search(/[ A-Za-z]/g);
        const inputValue = firstChar > -1 ? valueString.slice(0, firstChar) : valueString;
        const unitValue = firstChar > -1 ? valueString.slice(firstChar).trim() : '';
        return { inputValue: inputValue, unitValue: unitValue };
    };

    /**
     * Constructs a dynamic option label with a translated unit. When the unit
     * code is not present in the available options (e.g. a value imported with
     * an invalid/unknown code), the original unit code is preserved so it is
     * surfaced to the user instead of "undefined". Matches the legacy editor
     * behavior implemented in PhysicalAttributeService.formatValue.
     */
    const getLabel = (value: string) => {

        const valueAndUnit = splitToValueAndUnit(value);

        // If there's no unit in the value, just return the formatted numeric value
        if (!valueAndUnit.unitValue) {
            return formatFloat(valueAndUnit.inputValue);
        }

        const targetUnit = valueAndUnit.unitValue.toLowerCase();
        const unitOption = options.find(option => (option.key ?? option.name)?.toLowerCase() === targetUnit);
        const formattedInputValue = formatFloat(valueAndUnit.inputValue);
        const unitLabel = unitOption?.translatedOption || unitOption?.value || valueAndUnit.unitValue;
        const finalLabel = `${formattedInputValue} ${unitLabel}`;

        return finalLabel;

    };

    let dynamicOptions: OptionType[] = [];
    if (typedValue.trim() !== '') {
        // Create the dynamic options from only the number part, allowing
        // the user to include a unit and be searchable in the dynamic options:
        const typedValueAndUnit = splitToValueAndUnit(typedValue);
        dynamicOptions = options.map((option) => ({
            // Physical options have the value in their keys, its concept is flipped in the data model!!
            value: `${typedValueAndUnit.inputValue} ${option.key}`,
            label: `${typedValueAndUnit.inputValue} ${option.translatedOption || option.value}`,
        }));
    }

    let selectedOption: OptionType | undefined = undefined;
    if (selectedValue) {
        const valueAndUnit = splitToValueAndUnit(selectedValue);
        if (valueAndUnit.unitValue) {
            selectedOption = {
                value: selectedValue,
                label: getLabel(selectedValue)
            };
        }
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={false}
        >
            <div className="tw-flex tw-flex-row">
                <Controller
                    control={control}
                    name={fieldPath}
                    render={({ field }) =>
                        <Combobox
                            key={comboboxKey}
                            id={fieldPath}
                            className={className? className: ''}
                            inputControlClassnames={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} ${gridMode ? 'tw-bg-white' : 'tw-px-3'}`}
                            borderless={gridMode}
                            isDisabled={readonly}
                            placeholder={placeholder}
                            ref={comboboxRef}
                            isLoading={loadingOptions}
                            options={dynamicOptions}
                            value={selectedOption}
                            menuPortalTarget={document.body}
                            onChange={newValue => {
                                const value = normalizePhysicalComboboxValue(newValue);
                                markUserChange(value);
                                field.onChange(value);
                                if (!readonly) {
                                    requestAnimationFrame(() => comboboxRef.current?.focus?.());
                                }
                            }}
                            onFocus={handleUrlChange}
                            inputValue={typedValue}
                            isClearable={!gridMode && !readonly}
                            onInputChange={newInput => {
                                // Always format the floating point (dot <-> comma conversion) on user input:
                                const formattedNewInput = formatFloat(newInput);
                                setTypedValue(formattedNewInput);
                            }}
                            filterOption={(option, inputValue) => {
                                // Allow options to be filtered even without having a space between the value and the unit:
                                // ex: 123 kg -> will be found and filtered
                                // ex: 123kg  -> will also be found and filtered
                                const normalizedLabel = option.label.toLowerCase().replace(/ /g, '');
                                const normalizedInput = inputValue.toLowerCase();
                                return option.label.toLowerCase().includes(normalizedInput) ||
                                    normalizedLabel.includes(normalizedInput);
                            }}
                        />
                    }
                />
            </div>
        </Layout>
    );
};

export { PhysicalComponent };
